import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Eye, Save, AlertTriangle } from 'lucide-react';
import QuestionEditor from './QuestionEditor';

// Полный билдер квиза. Контролируемый: внешнее состояние = source of truth.
// Поддерживает create- и edit-сценарии (см. QuizCreatePage / QuizEditPage).

const blankQuestion = () => ({
  text: '',
  options: [{ text: '' }, { text: '' }],
  correctOptionIndex: 0,
  explanation: '',
});

const QuizEditor = ({
  value,
  onChange,
  onSubmit,
  isSubmitting,
  submitLabel,
  banner,
}) => {
  const { t } = useTranslation();
  const [showPreview, setShowPreview] = useState(false);
  const resolvedSubmitLabel = submitLabel || t('quiz.editor.save');

  const setField = (patch) => onChange({ ...value, ...patch });
  const setQuestion = (idx, q) =>
    setField({ questions: value.questions.map((qq, i) => (i === idx ? q : qq)) });

  const addQuestion = () => {
    if (value.questions.length >= 30) return;
    setField({ questions: [...value.questions, blankQuestion()] });
  };
  const removeQuestion = (idx) => {
    if (value.questions.length <= 1) return;
    setField({ questions: value.questions.filter((_, i) => i !== idx) });
  };
  const moveQuestion = (idx, dir) => {
    const next = [...value.questions];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setField({ questions: next });
  };

  // Локальная валидация — те же правила, что и на сервере; ошибки в один баннер.
  const errors = (() => {
    const out = [];
    if (!value.title?.trim()) out.push(t('quiz.editor.errors.needTitle'));
    if (!value.questions || value.questions.length < 1) out.push(t('quiz.editor.errors.needQuestion'));
    (value.questions || []).forEach((q, i) => {
      const n = i + 1;
      if (!q.text?.trim()) out.push(t('quiz.editor.errors.questionNeedText', { n }));
      if (!q.options || q.options.length < 2) out.push(t('quiz.editor.errors.questionNeed2Options', { n }));
      if (q.options?.some((o) => !o?.text?.trim())) out.push(t('quiz.editor.errors.questionAllOptionsFilled', { n }));
      if (!Number.isInteger(q.correctOptionIndex) || q.correctOptionIndex < 0 || q.correctOptionIndex >= q.options.length) {
        out.push(t('quiz.editor.errors.questionMarkCorrect', { n }));
      }
    });
    return out;
  })();

  return (
    <div className="space-y-6">
      {banner}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 sm:p-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {t('quiz.editor.titleLabel')}
        </label>
        <input
          type="text"
          value={value.title || ''}
          onChange={(e) => setField({ title: e.target.value })}
          maxLength={200}
          placeholder={t('quiz.editor.titlePlaceholder')}
          className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-gray-400 break-words"
        />

        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mt-4 mb-2">
          {t('quiz.editor.descLabel')}
        </label>
        <textarea
          value={value.description || ''}
          onChange={(e) => setField({ description: e.target.value })}
          rows={3}
          maxLength={2000}
          placeholder={t('quiz.editor.descPlaceholder')}
          className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 placeholder:text-gray-400 break-words"
        />
      </div>

      {/* Questions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            {t('quiz.editor.questionsHeader', { count: value.questions?.length || 0 })}
          </h2>
          <button
            type="button"
            onClick={() => setShowPreview((s) => !s)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Eye className="w-4 h-4" /> {showPreview ? t('quiz.editor.edit') : t('quiz.editor.preview')}
          </button>
        </div>

        {showPreview ? (
          <QuizPreview value={value} />
        ) : (
          <>
            {(value.questions || []).map((q, i) => (
              <QuestionEditor
                key={i}
                index={i}
                total={value.questions.length}
                question={q}
                onChange={(qq) => setQuestion(i, qq)}
                onMoveUp={() => moveQuestion(i, -1)}
                onMoveDown={() => moveQuestion(i, +1)}
                onRemove={() => removeQuestion(i)}
              />
            ))}
            <button
              type="button"
              onClick={addQuestion}
              disabled={value.questions?.length >= 30}
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 py-4 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" /> {t('quiz.editor.addQuestion')}
            </button>
          </>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {t('quiz.editor.beforeSave')}
              </p>
              <ul className="mt-1 list-disc pl-5 space-y-0.5 text-sm text-amber-700 dark:text-amber-300">
                {errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Submit */}
      <div className="sticky bottom-4 z-10 flex justify-end">
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting || errors.length > 0}
          className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Save className="w-4 h-4" />
          {isSubmitting ? t('quiz.editor.saving') : resolvedSubmitLabel}
        </button>
      </div>
    </div>
  );
};

const QuizPreview = ({ value }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-5">
      <div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white break-words">
          {value.title || t('quiz.editor.untitled')}
        </h3>
        {value.description && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 break-words [overflow-wrap:anywhere]">
            {value.description}
          </p>
        )}
      </div>
      {(value.questions || []).map((q, i) => (
        <div key={i} className="rounded-xl bg-white dark:bg-gray-900 p-4 border border-gray-200 dark:border-gray-700">
          <p className="font-semibold text-gray-900 dark:text-white mb-2 break-words">
            {i + 1}. {q.text || t('quiz.editor.emptyQuestion')}
          </p>
          <div className="space-y-1.5">
            {(q.options || []).map((o, k) => (
              <div
                key={k}
                className={`text-sm px-3 py-2 rounded-lg border break-words ${
                  k === q.correctOptionIndex
                    ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/20 text-emerald-800 dark:text-emerald-200'
                    : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {o.text || t('quiz.editor.optionPlaceholder', { n: k + 1 })}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default QuizEditor;
